import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { createSignature, listSignatures } from "@/api/client";
import { useSession } from "@/auth/session";
import { formatDate } from "@/components/format";
import { EmptyState, ErrorBanner, ToolbarButton, WorkspaceHeader } from "@/components/ui";
import { ScrollArea } from "@/components/ScrollArea";

export function SignatureList() {
  const { can } = useSession();
  const signatures = useQuery({ queryKey: ["signatures"], queryFn: listSignatures });
  const items = signatures.data ?? [];

  return (
    <section className="space-y-5">
      <WorkspaceHeader title="Signatures" detail="Signatories and their dated signature images" />
      <ErrorBanner error={signatures.error} />
      {can("signature.manage") ? <CreateSignatureForm /> : null}

      <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
        {signatures.isLoading ? (
          <EmptyState title="Loading signatures" detail="Fetching signatories." />
        ) : signatures.isSuccess && items.length === 0 ? (
          <EmptyState title="No signatures yet" detail="Add a signatory, then upload their signature image." />
        ) : (
          <ScrollArea label="Signatures">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead className="border-b border-zinc-200 bg-zinc-50 text-xs uppercase text-zinc-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Signatory</th>
                  <th className="px-4 py-3 font-medium">Role</th>
                  <th className="px-4 py-3 font-medium">Current version</th>
                  <th className="px-4 py-3 font-medium">In effect from</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {items.map((signature) => (
                  <tr key={signature.id} className="hover:bg-zinc-50">
                    <td className="px-4 py-3">
                      <Link
                        to="/signatures/$signatureId"
                        params={{ signatureId: signature.id }}
                        className="font-medium text-zinc-950 underline-offset-2 hover:underline"
                      >
                        {signature.signatory_name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-zinc-700">{signature.role_title ?? "-"}</td>
                    <td className="px-4 py-3">
                      {signature.current_version ? (
                        <span className="font-medium">v{signature.current_version.version_no}</span>
                      ) : (
                        <span className="text-zinc-500">No image yet</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-zinc-500">{formatDate(signature.current_version?.effective_from)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        )}
      </div>
    </section>
  );
}

function CreateSignatureForm() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [role, setRole] = useState("");

  const create = useMutation({
    mutationFn: () =>
      createSignature({ signatory_name: name.trim(), ...(role.trim() ? { role_title: role.trim() } : {}) }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ["signatures"] });
      // A new signatory has no image; their page is where the first one is uploaded.
      await navigate({ to: "/signatures/$signatureId", params: { signatureId: created.id } });
    },
  });

  const field = "mt-1 w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand";
  return (
    <div className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <h2 className="text-sm font-semibold">New signatory</h2>
      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end">
        <label className="block text-sm">
          <span className="font-medium text-zinc-700">Signatory name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className={field} />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-zinc-700">Role title</span>
          <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="e.g. Registrar" className={field} />
        </label>
        <ToolbarButton tone="primary" disabled={name.trim() === "" || create.isPending} onClick={() => create.mutate()}>
          {create.isPending ? "Creating..." : "Create signature"}
        </ToolbarButton>
      </div>
      <ErrorBanner error={create.error} />
    </div>
  );
}
